import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Shield, Database, Zap, Clock, ExternalLink, Copy, Check, ChevronDown, ChevronRight } from 'lucide-react'
import './Admin.css'

const TABS = ['Overview', 'Schema', 'Prompts', 'Changelog']

const LINKS = [
  { label: 'Live App', url: 'https://inventory.decentstartup.com' },
  { label: 'Supabase', url: 'https://supabase.com/dashboard/project/xkvuubfovepbsgfhdycx' },
  { label: 'GitHub', url: 'https://github.com/JJonasDecent/Inventory-Web-App' },
  { label: 'Vercel', url: 'https://vercel.com' },
]

const SCHEMA = [
  {
    name: 'brands',
    description: 'The three brands. Pre-populated — you shouldn\'t need to change this.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier (auto-generated)' },
      { name: 'name', type: 'text', desc: 'Brand name e.g. "Bloomi"' },
      { name: 'slug', type: 'text', desc: 'URL-safe version e.g. "bloomi"' },
      { name: 'category', type: 'text', desc: 'Category tag e.g. "health", "home", "intimate"' },
      { name: 'created_at', type: 'timestamp', desc: 'When the record was created' },
    ]
  },
  {
    name: 'user_profiles',
    description: 'One record per user. Auto-created when someone signs up via Supabase Auth.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Matches the Supabase auth user ID' },
      { name: 'email', type: 'text', desc: 'User\'s email address' },
      { name: 'full_name', type: 'text', desc: 'Display name' },
      { name: 'role', type: 'text', desc: '"super_admin", "brand_super_admin", "brand_admin", or "viewer"' },
      { name: 'created_at', type: 'timestamp', desc: 'When the profile was created' },
    ]
  },
  {
    name: 'user_brand_access',
    description: 'Controls which brands each user can see. Super admins bypass this and see everything.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'user_id', type: 'uuid', desc: 'Links to user_profiles.id' },
      { name: 'brand_id', type: 'uuid', desc: 'Links to brands.id' },
    ]
  },
  {
    name: 'products',
    description: 'Your product catalog / SKU list. Scoped per brand. Includes finished goods, components, raw materials, and virtual bundles.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand this product belongs to' },
      { name: 'sku', type: 'text', desc: 'SKU code — must be unique within a brand' },
      { name: 'name', type: 'text', desc: 'Product name' },
      { name: 'description', type: 'text', desc: 'Optional description' },
      { name: 'category', type: 'text', desc: 'Product category' },
      { name: 'unit_of_measure', type: 'text', desc: 'e.g. "each", "case", "pair"' },
      { name: 'product_type', type: 'text', desc: '"finished_good", "component", "raw_material", "virtual_bundle", "physical_bundle"' },
      { name: 'shopify_enabled', type: 'boolean', desc: 'Whether this SKU sells on Shopify' },
      { name: 'shopify_replen_status', type: 'text', desc: '"replenish", "sunset", or "new"' },
      { name: 'amazon_enabled', type: 'boolean', desc: 'Whether this SKU sells on Amazon' },
      { name: 'amazon_replen_status', type: 'text', desc: '"replenish", "sunset", or "new"' },
      { name: 'channels', type: 'text', desc: '"shopify", "amazon", or "both"' },
      { name: 'sourcing_type', type: 'text', desc: '"overseas" (45 day transit) or "domestic" (7 day transit)' },
      { name: 'production_lead_time_days', type: 'integer', desc: 'Production lead time in days — used for reorder alerts' },
      { name: 'target_days_of_supply', type: 'integer', desc: 'Target days of inventory to maintain (default 90)' },
      { name: 'fba_target_days', type: 'integer', desc: 'Target days of FBA inventory (default 60)' },
      { name: 'cost_price', type: 'decimal', desc: 'Your cost / landed cost' },
      { name: 'retail_price', type: 'decimal', desc: 'Retail selling price' },
      { name: 'reorder_point', type: 'integer', desc: 'Qty threshold that triggers a reorder alert' },
      { name: 'supplier_id', type: 'uuid', desc: 'Default supplier — links to suppliers.id' },
      { name: 'asin', type: 'text', desc: 'Amazon ASIN' },
      { name: 'upc', type: 'text', desc: 'UPC barcode' },
      { name: 'weight_oz', type: 'decimal', desc: 'Weight in ounces' },
      { name: 'length_in', type: 'decimal', desc: 'Length in inches' },
      { name: 'width_in', type: 'decimal', desc: 'Width in inches' },
      { name: 'height_in', type: 'decimal', desc: 'Height in inches' },
      { name: 'is_active', type: 'boolean', desc: 'Whether this SKU is active' },
      { name: 'deleted_at', type: 'timestamp', desc: 'Soft delete timestamp' },
      { name: 'created_at', type: 'timestamp', desc: 'When the record was created' },
    ]
  },
  {
    name: 'bill_of_materials',
    description: 'Links virtual/physical bundle SKUs to their component SKUs. Each row is one component in one bundle.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'parent_product_id', type: 'uuid', desc: 'The bundle SKU — links to products.id' },
      { name: 'component_product_id', type: 'uuid', desc: 'The component SKU — links to products.id' },
      { name: 'quantity', type: 'decimal', desc: 'How many of this component per bundle' },
      { name: 'created_at', type: 'timestamp', desc: 'When the record was created' },
    ]
  },
  {
    name: 'sell_through_rates',
    description: 'Sell-through rate data per product. Updated automatically via Shopify orders sync or manually.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'product_id', type: 'uuid', desc: 'Links to products.id' },
      { name: 'shopify_units_28d', type: 'decimal', desc: 'Units sold on Shopify in last 28 days' },
      { name: 'amazon_units_14d', type: 'decimal', desc: 'Units sold on Amazon in last 14 days' },
      { name: 'shopify_str_source', type: 'text', desc: '"manual" or "shopify_auto" — whether Shopify rate was entered manually or pulled from orders' },
      { name: 'updated_at', type: 'timestamp', desc: 'When last updated' },
      { name: 'updated_by', type: 'uuid', desc: 'Which user last updated (null if auto-synced)' },
    ]
  },
  {
    name: 'locations',
    description: 'Warehouse and fulfillment locations. Type determines which inventory view they appear in.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'name', type: 'text', desc: 'Location name e.g. "ShipBob LA"' },
      { name: 'type', type: 'text', desc: '"1pl", "3pl" (warehouse view), "amazon_fba" (FBA view), "supplier"' },
      { name: 'address', type: 'text', desc: 'Physical address' },
      { name: 'notes', type: 'text', desc: 'Any notes' },
      { name: 'is_active', type: 'boolean', desc: 'Whether this location is active' },
    ]
  },
  {
    name: 'inventory_on_hand',
    description: 'Current on-hand quantity per product per location. Updated automatically via inventory_transactions trigger.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'product_id', type: 'uuid', desc: 'Links to products.id' },
      { name: 'location_id', type: 'uuid', desc: 'Links to locations.id' },
      { name: 'on_hand', type: 'decimal', desc: 'Current quantity on hand' },
    ]
  },
  {
    name: 'inventory_transactions',
    description: 'Every inventory movement. The source of truth that drives inventory_on_hand.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'product_id', type: 'uuid', desc: 'Links to products.id' },
      { name: 'location_id', type: 'uuid', desc: 'Links to locations.id' },
      { name: 'transaction_type', type: 'text', desc: '"received", "sold", "returned", "damaged", "correction", "transfer_in", "transfer_out", "shopify_correction", etc.' },
      { name: 'quantity', type: 'decimal', desc: 'Positive = added, negative = removed' },
      { name: 'notes', type: 'text', desc: 'Reason or description' },
      { name: 'source', type: 'text', desc: '"manual" or "shopify"' },
      { name: 'reference_id', type: 'uuid', desc: 'Links to transfer_orders.id or other reference' },
      { name: 'created_by', type: 'uuid', desc: 'Which user created this transaction' },
      { name: 'created_at', type: 'timestamp', desc: 'When the transaction occurred' },
    ]
  },
  {
    name: 'brand_integrations',
    description: 'Shopify OAuth credentials per brand. One row per brand per integration type.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'integration_type', type: 'text', desc: '"shopify"' },
      { name: 'store_url', type: 'text', desc: 'Shopify store URL e.g. "the-bloomi.myshopify.com"' },
      { name: 'client_id', type: 'text', desc: 'Shopify app client ID' },
      { name: 'client_secret', type: 'text', desc: 'Shopify app client secret' },
      { name: 'access_token', type: 'text', desc: 'OAuth access token — set after OAuth flow completes' },
      { name: 'is_active', type: 'boolean', desc: 'Whether this integration is active' },
      { name: 'last_synced_at', type: 'timestamp', desc: 'When inventory was last synced' },
    ]
  },
  {
    name: 'shopify_sync_log',
    description: 'Log of every Shopify sync run — both inventory and STR syncs.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'sync_type', type: 'text', desc: '"inventory" or "str"' },
      { name: 'status', type: 'text', desc: '"running", "success", or "error"' },
      { name: 'products_synced', type: 'integer', desc: 'Number of products processed' },
      { name: 'corrections_made', type: 'integer', desc: 'Inventory: corrections made. STR: SKUs with sales found.' },
      { name: 'error_message', type: 'text', desc: 'Error details if status = error' },
      { name: 'started_at', type: 'timestamp', desc: 'When the sync started' },
      { name: 'completed_at', type: 'timestamp', desc: 'When the sync finished' },
    ]
  },
  {
    name: 'shopify_location_map',
    description: 'Maps Shopify location IDs to our internal location IDs.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'shopify_location_id', type: 'text', desc: 'Shopify location ID' },
      { name: 'shopify_location_name', type: 'text', desc: 'Shopify location name' },
      { name: 'our_location_id', type: 'uuid', desc: 'Links to locations.id' },
    ]
  },
  {
    name: 'suppliers',
    description: 'Vendor/supplier records. Scoped per brand.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand this supplier belongs to' },
      { name: 'name', type: 'text', desc: 'Company name' },
      { name: 'contact_name', type: 'text', desc: 'Primary contact person' },
      { name: 'email', type: 'text', desc: 'Contact email' },
      { name: 'phone', type: 'text', desc: 'Contact phone' },
      { name: 'address', type: 'text', desc: 'Physical address' },
      { name: 'country', type: 'text', desc: 'Country of origin' },
      { name: 'payment_terms', type: 'text', desc: 'e.g. "Net 30", "50% deposit, 50% on shipment"' },
      { name: 'lead_time_days', type: 'integer', desc: 'Typical lead time in days' },
      { name: 'notes', type: 'text', desc: 'Any additional notes' },
      { name: 'created_at', type: 'timestamp', desc: 'When the record was created' },
    ]
  },
  {
    name: 'purchase_orders',
    description: 'PO headers. One record per PO.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'po_number', type: 'text', desc: 'Your PO number — must be unique within a brand' },
      { name: 'supplier_id', type: 'uuid', desc: 'Links to suppliers.id' },
      { name: 'status', type: 'text', desc: '"draft", "sent", "confirmed", "partially_received", "received", "cancelled"' },
      { name: 'order_date', type: 'date', desc: 'Date PO was placed' },
      { name: 'expected_date', type: 'date', desc: 'Expected arrival date' },
      { name: 'notes', type: 'text', desc: 'Any PO-level notes' },
      { name: 'created_by', type: 'uuid', desc: 'Which user created this PO' },
      { name: 'created_at', type: 'timestamp', desc: 'When the record was created' },
    ]
  },
  {
    name: 'po_line_items',
    description: 'Individual line items within a PO.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'po_id', type: 'uuid', desc: 'Links to purchase_orders.id' },
      { name: 'product_id', type: 'uuid', desc: 'Links to products.id' },
      { name: 'quantity_ordered', type: 'decimal', desc: 'Qty on the PO' },
      { name: 'quantity_received', type: 'decimal', desc: 'Qty actually received so far' },
      { name: 'unit_cost', type: 'decimal', desc: 'Cost per unit' },
      { name: 'total_cost', type: 'decimal', desc: 'Auto-calculated: quantity × unit cost' },
    ]
  },
  {
    name: 'shipments',
    description: 'Inbound shipment records. Can be linked to a PO or standalone.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'po_id', type: 'uuid', desc: 'Linked PO (optional)' },
      { name: 'shipment_number', type: 'text', desc: 'Your internal shipment reference' },
      { name: 'carrier', type: 'text', desc: 'Shipping carrier e.g. "FedEx", "DHL"' },
      { name: 'tracking_number', type: 'text', desc: 'Carrier tracking number' },
      { name: 'ship_date', type: 'date', desc: 'Date shipment left the supplier' },
      { name: 'expected_arrival', type: 'date', desc: 'Expected delivery date' },
      { name: 'actual_arrival', type: 'date', desc: 'Actual date received' },
      { name: 'status', type: 'text', desc: '"in_transit", "arrived", "receiving", "received", "exception"' },
      { name: 'ship_to_location_id', type: 'uuid', desc: 'Destination location — links to locations.id' },
      { name: 'is_direct_ship', type: 'boolean', desc: 'Whether this ships directly to FBA' },
      { name: 'notes', type: 'text', desc: 'Any shipment notes' },
      { name: 'created_at', type: 'timestamp', desc: 'When the record was created' },
    ]
  },
  {
    name: 'shipment_line_items',
    description: 'Items received within a shipment.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'shipment_id', type: 'uuid', desc: 'Links to shipments.id' },
      { name: 'product_id', type: 'uuid', desc: 'Links to products.id' },
      { name: 'po_line_item_id', type: 'uuid', desc: 'Links to po_line_items.id (optional)' },
      { name: 'quantity_received', type: 'decimal', desc: 'Qty physically received' },
      { name: 'lot_number', type: 'text', desc: 'Manufacturer lot or batch number' },
      { name: 'expiration_date', type: 'date', desc: 'Product expiration date' },
      { name: 'notes', type: 'text', desc: 'Any receiving notes or discrepancies' },
    ]
  },
  {
    name: 'transfer_orders',
    description: 'Warehouse-to-FBA or warehouse-to-warehouse transfer orders.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'transfer_number', type: 'text', desc: 'Transfer reference number' },
      { name: 'from_location_id', type: 'uuid', desc: 'Source location — links to locations.id' },
      { name: 'to_location_id', type: 'uuid', desc: 'Destination location — links to locations.id' },
      { name: 'status', type: 'text', desc: '"planned", "shipped", "cancelled" — no receiving; arrival comes from inventory uploads' },
      { name: 'ship_date', type: 'date', desc: 'Date transferred' },
      { name: 'expected_arrival', type: 'date', desc: 'Expected arrival date' },
      { name: 'notes', type: 'text', desc: 'Any notes' },
      { name: 'created_by', type: 'uuid', desc: 'Which user created this transfer' },
    ]
  },
  {
    name: 'invoices',
    description: 'Supplier invoices and payment tracking.',
    columns: [
      { name: 'id', type: 'uuid', desc: 'Unique identifier' },
      { name: 'brand_id', type: 'uuid', desc: 'Which brand' },
      { name: 'supplier_id', type: 'uuid', desc: 'Links to suppliers.id' },
      { name: 'po_id', type: 'uuid', desc: 'Linked PO (optional)' },
      { name: 'invoice_number', type: 'text', desc: 'Supplier\'s invoice number' },
      { name: 'invoice_date', type: 'date', desc: 'Date on the invoice' },
      { name: 'due_date', type: 'date', desc: 'Payment due date' },
      { name: 'amount', type: 'decimal', desc: 'Total invoice amount' },
      { name: 'amount_paid', type: 'decimal', desc: 'Amount paid so far' },
      { name: 'status', type: 'text', desc: '"unpaid", "partially_paid", "paid", "overdue", "disputed"' },
      { name: 'payment_date', type: 'date', desc: 'Date payment was made' },
      { name: 'payment_reference', type: 'text', desc: 'Wire reference, check number, etc.' },
      { name: 'notes', type: 'text', desc: 'Any notes' },
      { name: 'file_url', type: 'text', desc: 'Link to invoice document' },
      { name: 'created_at', type: 'timestamp', desc: 'When the record was created' },
    ]
  },
]

const PROMPTS = [
  {
    category: 'Adding Fields',
    items: [
      { label: 'Add a field to an existing table', prompt: 'I want to add a field for [FIELD NAME] to the [TABLE NAME] table so I can track [WHAT YOU\'RE TRACKING]. Here is the current schema: [paste schema section].' },
      { label: 'Add a dropdown/enum field', prompt: 'I want to add a [FIELD NAME] field to the [TABLE NAME] table with these options: [option1, option2, option3]. It should default to [DEFAULT VALUE].' },
    ]
  },
  {
    category: 'Adding Modules',
    items: [
      { label: 'Add a new module/page', prompt: 'I want to add a [MODULE NAME] module to the app. It should track [DESCRIBE WHAT IT TRACKS]. It should be scoped per brand like the other modules. Here is the current schema for context: [paste relevant tables].' },
      { label: 'Add a returns module', prompt: 'I want to add a Returns module. It should link back to shipments and products, track reason codes, quantities, and disposition (restock, destroy, return to supplier).' },
      { label: 'Add a replenishment queue', prompt: 'I want a Replenishment page that shows every product with a reorder alert in one actionable list. It should show SKU, name, suggested order qty, lead time, supplier, and a button to create a draft PO directly from the alert.' },
    ]
  },
  {
    category: 'CSV Imports',
    items: [
      { label: 'Import Amazon FBA inventory', prompt: 'I want to upload an Amazon FBA Inventory report CSV and have it create inventory correction transactions for each SKU. Here is the inventory schema: [paste inventory_transactions and inventory_on_hand tables]. The CSV columns are: [paste column headers from your report].' },
      { label: 'Import Amazon sell-through rates', prompt: 'I want to upload an Amazon Business Reports CSV and have it update the amazon_units_14d field in sell_through_rates for each SKU. Here is the sell_through_rates schema: [paste]. The CSV columns are: [paste column headers].' },
      { label: 'Import catalog from CSV', prompt: 'I want to upload a CSV to bulk-import products into the catalog for a new brand. The CSV should have columns for: SKU, Name, Category, Product Type, Shopify Enabled, Amazon Enabled, Cost Price, Retail Price, Supplier Name, Sourcing Type, Production Lead Time Days. Here is the products schema: [paste].' },
    ]
  },
  {
    category: 'Integrations',
    items: [
      { label: 'Connect a new brand to Shopify', prompt: 'I want to connect [BRAND NAME] to their Shopify store at [STORE URL]. I need to set up the OAuth flow and add their credentials to the brand_integrations table. Here is how Bloomi is configured: [paste brand_integrations row].' },
      { label: 'Add Amazon SP-API sync', prompt: 'I want to pull live FBA inventory from Amazon using the SP-API for [BRAND NAME]. Their seller credentials are: [paste]. I want it to work similarly to the Shopify inventory sync — create correction transactions when quantities differ.' },
    ]
  },
  {
    category: 'AI & Automation',
    items: [
      { label: 'Auto-draft reorder POs', prompt: 'I want an AI agent that checks the catalog for products at or below their reorder point and drafts a purchase order for each one automatically. It should group by supplier. Here is the schema: [paste products and purchase_orders tables].' },
      { label: 'Expiration alerts via Slack', prompt: 'I want an automated Slack message sent every Monday morning listing any lots expiring within 60 days, grouped by brand. Here is the shipment_line_items schema: [paste].' },
      { label: 'Bundle STR explosion', prompt: 'I want the sell-through rate for component SKUs to include implied demand from virtual bundles. For each component, add up (bundle STR × quantity in BOM) across all bundles that contain it, then add the component\'s own direct Shopify sales. Here is the bill_of_materials and sell_through_rates schema: [paste].' },
    ]
  },
  {
    category: 'Reporting',
    items: [
      { label: 'Add a reporting page', prompt: 'I want to add a Reports page that shows: total spend by supplier (last 90 days), open PO value by brand, and average lead time by supplier. Here is the schema: [paste purchase_orders, po_line_items, suppliers].' },
      { label: 'Export to CSV', prompt: 'I want to be able to export the [TABLE NAME] table to a CSV file from within the app. It should export all records for the active brand.' },
    ]
  },
]

const CHANGELOG = [
  {
    date: 'April 20, 2026',
    version: 'v4.0',
    items: [
      'Virtual bundles — new product type with Bill of Materials editor in Catalog',
      'BOM table (bill_of_materials) with RLS policies — links bundle SKUs to component SKUs',
      'Bloomi virtual bundles loaded: BSTARTER, BQUICKIE, BGLOW, BVIBE, BSENSUAL, BESSENTIALS, BFLIRT, BLUBE, BMETIME, BZEN, BTRAVEL, BSENSORY, BORGASM, BLOVE, BINTIMATE (41 BOM rows)',
      'Virtual bundles excluded from warehouse/FBA/alerts inventory views',
      'Shopify STR auto-sync — pulls 28-day fulfilled orders via read_orders scope, updates sell_through_rates',
      'STR source tracking — shopify_str_source column ("manual" vs "shopify_auto"), Auto badge in Inventory',
      'Daily STR cron at 7am UTC — runs alongside existing 6am inventory cron',
      'Sync history improvements — sync_type column differentiates Inventory vs STR syncs with color-coded badges',
      'Per-channel replenishment status — shopify_replen_status and amazon_replen_status per product',
      'Sales channel toggles in Catalog product form — Shopify and Amazon enabled/disabled per SKU',
    ]
  },
  {
    date: 'April 17, 2026',
    version: 'v3.0',
    items: [
      'Shopify inventory sync — OAuth flow, location mapping, correction transactions',
      'Daily Vercel cron job for automatic Shopify sync at 6am UTC',
      'Reorder alerts — per-product lead time, sourcing type (domestic/overseas), 30-day buffer',
      'FBA transfer alerts — fires when FBA days on hand drops below target (default 60d)',
      'Smart net supply calculation — reorder accounts for FBA transfer needed before triggering',
      'Dynamic inventory tabs — one tab per location, named by actual location name',
      'Warehouse view — Shopify sell-through rate only for days on hand',
      'FBA view — Amazon sell-through rate only for days on hand',
      'Location-specific in-transit — only shows shipments headed to that location',
      'Alerts tab — unified view with total supply, FBA days, FBA transfer, net supply, reorder',
      'New catalog fields — sourcing type, production lead time, target days of supply, FBA target days',
      'Shopify Sync page in sidebar for super admins',
    ]
  },
  {
    date: 'April 16, 2026',
    version: 'v2.0',
    items: [
      'Inventory module — transaction ledger, on-hand view, adjustments',
      'Transfer orders — planned → shipped (or cancelled); no inventory transactions, uploads are the source of truth',
      'Sell-through rates — manual entry per product (Shopify 28-day, Amazon 14-day)',
      'Inventory summary — on hand, in transit, on order, days on hand per location',
      'Shipments improvements — expected units, actual units, PO coverage table',
      'PO detail — cross-shipment coverage showing total shipped across all shipments',
      'Dashboard — inventory alerts panel, expiring within 90 days',
      'Locations — 1PL, 3PL, Amazon FBA, Supplier/Co-Mfr types',
    ]
  },
  {
    date: 'April 14, 2026',
    version: 'v1.0',
    items: [
      'Initial build — full app designed and deployed in one session',
      'Supabase database with RLS, multi-brand support',
      'User auth with role-based access (super admin, brand admin, viewer)',
      'Catalog, Suppliers, Purchase Orders, Shipments, Invoices modules',
      'Admin panel with overview, schema, prompts, changelog',
      'Deployed to Vercel with automatic GitHub deploys',
    ]
  }
]

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button className="btn btn-ghost btn-sm" onClick={copy} style={{ marginLeft: 'auto', flexShrink: 0 }}>
      {copied ? <Check size={13} style={{ color: 'var(--success)' }} /> : <Copy size={13} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

function SchemaTable({ table, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="schema-table">
      <button className="schema-table-header" onClick={() => setOpen(o => !o)}>
        <span className="mono" style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{table.name}</span>
        <span style={{ fontSize: '12px', color: 'var(--text-3)', flex: 1, marginLeft: '12px', textAlign: 'left' }}>{table.description}</span>
        <span className="badge badge-neutral" style={{ marginRight: '8px' }}>{table.columns.length} fields</span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Column</th><th>Type</th><th>Description</th></tr>
            </thead>
            <tbody>
              {table.columns.map(col => (
                <tr key={col.name}>
                  <td><span className="mono">{col.name}</span></td>
                  <td><span className="mono" style={{ color: 'var(--accent)', fontSize: '11px' }}>{col.type}</span></td>
                  <td style={{ color: 'var(--text-2)' }}>{col.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function Admin() {
  const { profile, brands } = useAuth()
  const [tab, setTab] = useState('Overview')
  const [stats, setStats] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { if (tab === 'Overview') loadStats() }, [tab])

  async function loadStats() {
    setLoading(true)
    const results = await Promise.all(brands.map(async b => {
      const [
        { count: products },
        { count: suppliers },
        { count: openPOs },
        { count: shipments },
        { data: invoices }
      ] = await Promise.all([
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('brand_id', b.id).eq('is_active', true),
        supabase.from('suppliers').select('*', { count: 'exact', head: true }).eq('brand_id', b.id),
        supabase.from('purchase_orders').select('*', { count: 'exact', head: true }).eq('brand_id', b.id).in('status', ['draft', 'sent', 'confirmed', 'partially_received']),
        supabase.from('shipments').select('*', { count: 'exact', head: true }).eq('brand_id', b.id).in('status', ['in_transit', 'arrived', 'receiving']),
        supabase.from('invoices').select('amount, amount_paid').eq('brand_id', b.id).in('status', ['unpaid', 'partially_paid', 'overdue'])
      ])
      const outstanding = (invoices || []).reduce((sum, i) => sum + ((i.amount || 0) - (i.amount_paid || 0)), 0)
      return { brand: b, products, suppliers, openPOs, shipments, outstanding }
    }))
    setStats(results)
    setLoading(false)
  }

  if (profile?.role !== 'super_admin' && profile?.role !== 'brand_super_admin') {
    return <div className="empty-state"><Shield size={40} /><p>Super admin access required</p></div>
  }

  const brandColors = { 'bloomi': '#c47dd8', 'wamsutta': '#6c8ef7', 'suzanne-somers': '#4caf7d' }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Shield size={20} style={{ color: 'var(--accent)' }} />
          <h1>Admin</h1>
          <span className="badge badge-accent">Super Admin</span>
        </div>
      </div>

      <div className="admin-tabs">
        {TABS.map(t => (
          <button key={t} className={`admin-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === 'Overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div className="card">
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>Quick Links</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {LINKS.map(l => (
                <a key={l.label} href={l.url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                  <ExternalLink size={12} />{l.label}
                </a>
              ))}
            </div>
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : stats.map(({ brand, products, suppliers, openPOs, shipments, outstanding }) => (
            <div key={brand.id} className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: brandColors[brand.slug] || 'var(--accent)' }} />
                <h3 style={{ fontSize: '15px', fontWeight: 600 }}>{brand.name}</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                {[
                  { label: 'Active SKUs', value: products },
                  { label: 'Suppliers', value: suppliers },
                  { label: 'Open POs', value: openPOs },
                  { label: 'Inbound', value: shipments },
                  { label: 'Outstanding', value: `$${(outstanding || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}` },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: 'var(--bg-3)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
                    <div style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{label}</div>
                    <div style={{ fontSize: '20px', fontWeight: 600 }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="card">
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px' }}>Tech Stack</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { name: 'React', desc: 'Frontend — what you see in the browser. Code lives in src/ folder of your GitHub repo.' },
                { name: 'Supabase', desc: 'Database and login system. All data lives here. Project ID: xkvuubfovepbsgfhdycx' },
                { name: 'Vercel', desc: 'Hosting. Auto-redeploys every time you push code to GitHub. Crons: inventory at 6am UTC, STR at 7am UTC.' },
                { name: 'GitHub', desc: 'Code storage. Repo: JJonasDecent/Inventory-Web-App' },
                { name: 'Shopify', desc: 'Connected to Bloomi (the-bloomi.myshopify.com). Scopes: read_inventory, read_locations, read_products, read_orders.' },
              ].map(({ name, desc }) => (
                <div key={name} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span className="badge badge-accent" style={{ marginTop: '2px', flexShrink: 0 }}>{name}</span>
                  <span style={{ fontSize: '13px', color: 'var(--text-2)' }}>{desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px' }}>Data Entry Order</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                '1. Suppliers — add vendors first',
                '2. Catalog — add SKUs, set product type, channel settings, sourcing type and lead times',
                '3. Locations — add warehouse and FBA locations (type matters for inventory views)',
                '4. Purchase Orders — create POs, add line items from catalog',
                '5. Shipments — log inbound shipments, set destination, receive items',
                '6. Inventory — adjust on-hand quantities, run Shopify sync for auto STR',
                '7. Invoices — track payment status linked to POs and suppliers',
                '8. Shopify Sync — connect store, map locations, run inventory and STR syncs',
              ].map(step => (
                <div key={step} style={{ fontSize: '13px', color: 'var(--text-2)', padding: '8px 12px', background: 'var(--bg-3)', borderRadius: 'var(--radius)' }}>{step}</div>
              ))}
            </div>
          </div>

          <div className="card">
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px' }}>User Roles</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { role: 'super_admin', badge: 'badge-accent', desc: 'Sees all brands, can manage users, and access all admin tools (Shopify Sync, Lot Import, Amazon Import, Admin). Only you should have this.' },
                { role: 'brand_super_admin', badge: 'badge-accent', desc: 'Same admin tools as super admin (Shopify Sync, Lot Import, Amazon Import, Admin) — but scoped to their assigned brand(s) only. Cannot manage users.' },
                { role: 'brand_admin', badge: 'badge-info', desc: 'Full read/write access to their assigned brand(s).' },
                { role: 'viewer', badge: 'badge-neutral', desc: 'Read-only access to their assigned brand(s).' },
              ].map(({ role, badge, desc }) => (
                <div key={role} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span className={`badge ${badge}`} style={{ marginTop: '2px', flexShrink: 0 }}>{role.replace('_', ' ')}</span>
                  <span style={{ fontSize: '13px', color: 'var(--text-2)' }}>{desc}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '12px', padding: '10px 12px', background: 'var(--accent-bg)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text-2)' }}>
              To invite a user: go to Supabase → Authentication → Users → Invite user. Once they sign in, assign their role on the Users page.
            </div>
          </div>

          <div className="card">
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px' }}>Build Queue</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[
                'Amazon inventory CSV import — upload FBA Inventory report, create correction transactions',
                'Amazon STR CSV import — upload Business Reports CSV, update amazon_units_14d',
                'Catalog CSV import — template + importer for onboarding new brands',
                'Onboard Wamsutta (Shopify only) and Suzanne Somers (Shopify + Amazon)',
                'Replenishment queue — single actionable page',
                'Forecast with growth adjustment',
                'MOQ on reorder suggestions',
                'Amazon SP-API full sync (long term)',
                'Phase 2 bundles: assembly orders, component STR explosion, reorder calc with BOM',
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: 'var(--text-2)', padding: '8px 12px', background: 'var(--bg-3)', borderRadius: 'var(--radius)' }}>
                  <span style={{ color: 'var(--text-3)', flexShrink: 0, fontWeight: 600 }}>{i + 1}.</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SCHEMA */}
      {tab === 'Schema' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div className="card" style={{ padding: '14px 16px', marginBottom: '8px' }}>
            <p style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.6 }}>
              This is the complete database structure. When you want to add something new, share this with Claude and describe what you want in plain English — Claude will write the SQL and update the app.
            </p>
          </div>
          {SCHEMA.map((table, i) => <SchemaTable key={table.name} table={table} defaultOpen={i === 0} />)}
        </div>
      )}

      {/* PROMPTS */}
      {tab === 'Prompts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div className="card" style={{ padding: '14px 16px' }}>
            <p style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.6 }}>
              Copy these prompts and paste them into Claude along with the relevant schema from the Schema tab. Fill in the bracketed parts before sending.
            </p>
          </div>
          {PROMPTS.map(group => (
            <div key={group.category}>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>{group.category}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {group.items.map(item => (
                  <div key={item.label} className="card" style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{item.label}</span>
                      <CopyButton text={item.prompt} />
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-2)', background: 'var(--bg-3)', padding: '10px 12px', borderRadius: 'var(--radius)', fontFamily: 'var(--mono)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {item.prompt}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CHANGELOG */}
      {tab === 'Changelog' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {CHANGELOG.map(entry => (
            <div key={entry.version} className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <span className="badge badge-success">{entry.version}</span>
                <span style={{ fontSize: '13px', color: 'var(--text-3)' }}>{entry.date}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {entry.items.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: 'var(--text-2)' }}>
                    <span style={{ color: 'var(--success)', marginTop: '2px', flexShrink: 0 }}>✓</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="card" style={{ background: 'var(--bg-3)', borderStyle: 'dashed' }}>
            <p style={{ fontSize: '13px', color: 'var(--text-3)', textAlign: 'center' }}>Future updates will be logged here</p>
          </div>
        </div>
      )}
    </div>
  )
}
